import { useEffect, useState } from "react";
import { api, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Send, FileDown, Handshake, Loader2, Users, ArrowRight, Sparkles,
} from "lucide-react";

// Only these stages are eligible for dispo
const DISPO_STAGES = ["contract", "offer_sent", "negotiation"];

function scoreBadge(score, reasons) {
  const cls =
    score >= 70 ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
    score >= 40 ? "bg-blue-50 text-blue-700 border-blue-200" :
    "bg-neutral-100 text-neutral-500 border-neutral-200";
  const top = Array.isArray(reasons) ? reasons.slice(0, 2).join(" · ") : "";
  const title = top ? `Top reasons: ${top}` : `${score} match`;
  return (
    <span
      className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 cursor-help ${cls}`}
      title={title}
      data-testid="buyer-match-badge"
    >
      {score} match{top ? " ⓘ" : ""}
    </span>
  );
}

export default function Dispo() {
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);   // lead
  const [match, setMatch] = useState(null);         // {lead, buyers[], fee_suggestion}
  const [picked, setPicked] = useState(new Set());
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [alsoSms, setAlsoSms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blasts, setBlasts] = useState([]);
  const [asgOpen, setAsgOpen] = useState(false);
  const [asgBuyer, setAsgBuyer] = useState(null);
  const [asgFee, setAsgFee] = useState(10000);
  const [asgClose, setAsgClose] = useState("");
  // ---- Filters
  const [fCity, setFCity] = useState("");
  const [fMinArv, setFMinArv] = useState("");
  const [fStage, setFStage] = useState("all");

  const loadLeads = async () => {
    try {
      const { data } = await api.get("/leads");
      const eligible = (data || []).filter((l) => DISPO_STAGES.includes(l.stage));
      setLeads(eligible);
    } catch { /* silent */ }
  };
  useEffect(() => { loadLeads(); }, []);

  const pickLead = async (lead) => {
    setSelected(lead);
    setMatch(null);
    setPicked(new Set());
    try {
      const { data } = await api.post("/dispo/match", { lead_id: lead.id });
      setMatch(data);
    } catch { toast.error("Match failed"); }
    try {
      const { data } = await api.get("/dispo/blasts", { params: { lead_id: lead.id } });
      setBlasts(data || []);
    } catch { /* silent */ }
  };

  const toggle = (bid) => {
    setPicked((prev) => {
      const s = new Set(prev);
      s.has(bid) ? s.delete(bid) : s.add(bid);
      return s;
    });
  };
  const pickTop = (n) => {
    const ids = (match?.buyers || []).filter((b) => b.match_score > 0).slice(0, n).map((b) => b.id);
    setPicked(new Set(ids));
  };

  const blast = async () => {
    if (picked.size === 0) return toast.error("Pick at least one buyer");
    setBusy(true);
    try {
      const { data } = await api.post("/dispo/blast", {
        lead_id: selected.id,
        buyer_ids: [...picked],
        subject: subject || null,
        body: body || null,
        also_sms: alsoSms,
      });
      toast.success(`Sent ${data.sent_email} emails${data.sent_sms ? `, ${data.sent_sms} SMS` : ""}`);
      setPicked(new Set());
      const { data: b } = await api.get("/dispo/blasts", { params: { lead_id: selected.id } });
      setBlasts(b || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Blast failed"); }
    finally { setBusy(false); }
  };

  const openAssignment = (buyer) => {
    setAsgBuyer(buyer);
    // Pre-fill with AI-suggested fee if we have one; otherwise keep last value
    const suggested = match?.fee_suggestion?.suggested_fee;
    if (suggested && Number(suggested) > 0) setAsgFee(suggested);
    setAsgOpen(true);
  };

  const downloadAssignment = async () => {
    if (!asgBuyer || !selected) return;
    setBusy(true);
    try {
      const r = await api.post("/dispo/assignment-pdf",
        {
          lead_id: selected.id,
          buyer_id: asgBuyer.id,
          assignment_fee: Number(asgFee) || 0,
          close_date: asgClose || null,
        },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `assignment_${asgBuyer.name.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setAsgOpen(false);
    } catch { toast.error("PDF failed"); }
    finally { setBusy(false); }
  };

  const buyers = match?.buyers || [];
  const canBlast = buyers.length > 0;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
          Dispo Suite
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Match a locked-up contract to your buyer list by area, budget, and
          strategy. Blast the deck in one click. Generate the assignment PDF
          the moment you close.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: eligible leads */}
        <div className="card-flat p-3 space-y-1" data-testid="dispo-lead-list">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2 px-1">
            Ready to Dispo
          </div>
          {/* Filters */}
          <div className="px-1 mb-3 space-y-2" data-testid="dispo-filters">
            <input
              type="text"
              value={fCity}
              onChange={(e) => setFCity(e.target.value)}
              placeholder="Filter by city…"
              className="w-full text-xs border border-[var(--app-line)] rounded px-2 py-1.5 field-sharp"
              data-testid="dispo-filter-city"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                value={fMinArv}
                onChange={(e) => setFMinArv(e.target.value)}
                placeholder="Min ARV $"
                className="w-full text-xs border border-[var(--app-line)] rounded px-2 py-1.5 field-sharp"
                data-testid="dispo-filter-min-arv"
              />
              <select
                value={fStage}
                onChange={(e) => setFStage(e.target.value)}
                className="w-full text-xs border border-[var(--app-line)] rounded px-2 py-1.5 field-sharp"
                data-testid="dispo-filter-stage"
              >
                <option value="all">All stages</option>
                <option value="contract">Contract</option>
                <option value="offer_sent">Offer sent</option>
                <option value="negotiation">Negotiation</option>
              </select>
            </div>
            {(fCity || fMinArv || fStage !== "all") && (
              <button
                onClick={() => { setFCity(""); setFMinArv(""); setFStage("all"); }}
                className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
                data-testid="dispo-filter-clear"
              >
                Clear filters
              </button>
            )}
          </div>
          {(() => {
            const filtered = leads.filter((l) => {
              if (fStage !== "all" && l.stage !== fStage) return false;
              if (fCity && !(l.property_address || "").toLowerCase().includes(fCity.toLowerCase())) return false;
              if (fMinArv && Number(l.property_arv || 0) < Number(fMinArv)) return false;
              return true;
            });
            if (leads.length === 0) return (
              <div className="text-xs text-muted-foreground px-1 py-4">
                No leads in negotiation / offer sent / contract yet.
              </div>
            );
            if (filtered.length === 0) return (
              <div className="text-xs text-muted-foreground px-1 py-4">
                No leads match your filters.
              </div>
            );
            return filtered.map((l) => (
              <button
                key={l.id}
                onClick={() => pickLead(l)}
                data-testid={`dispo-lead-${l.id}`}
                className={`w-full text-left rounded-lg px-3 py-2 border transition-colors ${
                  selected?.id === l.id
                    ? "border-[var(--app-black)] bg-[var(--app-surface)]"
                    : "border-transparent hover:bg-[var(--app-surface)]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium truncate">
                    {l.property_address || l.name}
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-2">
                    {l.stage.replace("_", " ")}
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                  {money(l.offer_amount) || money(l.property_mao)} · ARV {money(l.property_arv)}
                </div>
              </button>
            ));
          })()}
        </div>

        {/* Right: match + blast */}
        <div className="lg:col-span-2 space-y-3">
          {!selected ? (
            <div className="card-flat p-6 text-center text-sm text-muted-foreground">
              Pick a lead on the left to match it against your buyer list.
            </div>
          ) : !match ? (
            <div className="card-flat p-6 text-center text-sm text-muted-foreground">
              <Loader2 size={16} className="animate-spin inline mr-2" /> Matching…
            </div>
          ) : (
            <>
              <div className="card-flat p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Deal on the table
                    </div>
                    <div className="font-display text-lg font-bold mt-1">
                      {selected.property_address}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Contract {money(selected.offer_amount)} · ARV {money(selected.property_arv)}
                      · MAO {money(selected.property_mao)}
                    </div>
                    {match?.fee_suggestion?.suggested_fee > 0 && (
                      <div
                        className="mt-2 inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 cursor-help"
                        title={(match.fee_suggestion.reasoning || []).join(" · ")}
                        data-testid="fee-suggestion-chip"
                      >
                        <Sparkles size={11} />
                        AI suggests <strong>{money(match.fee_suggestion.suggested_fee)}</strong> assignment fee
                        <span className="opacity-70">ⓘ</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => pickTop(3)} data-testid="dispo-pick-top-3">
                      <Sparkles size={12} className="mr-1" /> Pick top 3
                    </Button>
                    <Button variant="outline" onClick={() => pickTop(10)} data-testid="dispo-pick-top-10">
                      Pick top 10
                    </Button>
                  </div>
                </div>
              </div>

              <div className="card-flat p-0 overflow-hidden">
                <div className="px-4 py-2 border-b border-[var(--app-line)] bg-[var(--app-surface)] flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    Buyer matches · {buyers.length}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{picked.size} selected</div>
                </div>
                <div className="max-h-[45vh] overflow-y-auto" data-testid="dispo-buyer-list">
                  {buyers.length === 0 ? (
                    <div className="text-xs text-muted-foreground px-4 py-6 text-center">
                      No buyers on file. Add some in the Buyer List tab.
                    </div>
                  ) : (
                    buyers.map((b) => (
                      <div
                        key={b.id}
                        className={`flex items-start gap-3 border-b border-[var(--app-line)] last:border-b-0 px-4 py-2 ${
                          picked.has(b.id) ? "bg-blue-50" : ""
                        }`}
                        data-testid={`dispo-buyer-${b.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={picked.has(b.id)}
                          onChange={() => toggle(b.id)}
                          disabled={!b.email && !b.phone}
                          className="mt-1"
                          data-testid={`dispo-buyer-check-${b.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium truncate">{b.name}</div>
                            {scoreBadge(b.match_score, b.match_reasons)}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {b.email || "—"} · {b.phone || "—"} · Budget {money(b.budget_min)}–{money(b.budget_max)} · {b.strategy || "any"}
                          </div>
                          {b.match_reasons?.length > 0 && (
                            <div className="text-[10px] text-emerald-700 mt-0.5">
                              {b.match_reasons.join(" · ")}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => openAssignment(b)}
                          className="text-[10px] uppercase tracking-widest border border-[var(--app-line)] rounded-full px-2 py-1 hover:bg-white"
                          data-testid={`dispo-assign-${b.id}`}
                          title="Generate assignment PDF"
                        >
                          <FileDown size={10} className="inline mr-1" /> Assign
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Blast composer */}
              <div className="card-flat p-4 space-y-3" data-testid="dispo-composer">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                  Blast message (leave blank for smart default)
                </div>
                <div>
                  <Label className="text-xs">Subject</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                         placeholder={`New off-market deal: ${selected.property_address}`}
                         className="mt-1" data-testid="dispo-subject" />
                </div>
                <div>
                  <Label className="text-xs">Body</Label>
                  <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)}
                            className="mt-1 text-xs" placeholder="Leave blank to use the smart default with ARV/MAO/offer/motivation…"
                            data-testid="dispo-body" />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs">
                    <Switch checked={alsoSms} onCheckedChange={setAlsoSms} data-testid="dispo-also-sms" />
                    <span>Also SMS buyers with a short teaser</span>
                  </label>
                  <Button
                    onClick={blast}
                    disabled={!canBlast || picked.size === 0 || busy}
                    className="rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
                    data-testid="dispo-blast"
                  >
                    {busy ? <Loader2 size={13} className="animate-spin mr-2" /> : <Send size={13} className="mr-2" />}
                    Blast to {picked.size || 0} buyers
                  </Button>
                </div>
              </div>

              {blasts.length > 0 && (
                <div className="card-flat p-4">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                    Recent blasts
                  </div>
                  <div className="space-y-1">
                    {blasts.slice(0, 6).map((b) => (
                      <div key={b.id} className="text-xs flex items-center gap-2">
                        <Users size={11} className="text-muted-foreground" />
                        <span>{b.buyer_ids?.length || 0} buyers</span>
                        <ArrowRight size={11} className="text-muted-foreground" />
                        <span>{b.results?.sent_email || 0} email, {b.results?.sent_sms || 0} sms</span>
                        <span className="text-muted-foreground ml-auto">
                          {new Date(b.created_at).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Assignment PDF dialog */}
      <Dialog open={asgOpen} onOpenChange={setAsgOpen}>
        <DialogContent className="rounded-2xl" data-testid="dispo-assign-dialog">
          <DialogHeader>
            <DialogTitle>Generate Assignment</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              1-page assignment-of-contract PDF between you and {asgBuyer?.name || "the buyer"}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Assignment fee (paid to you at closing)</Label>
              <Input type="number" value={asgFee} onChange={(e) => setAsgFee(e.target.value)}
                     className="mt-1" data-testid="dispo-assign-fee" />
              {match?.fee_suggestion?.suggested_fee > 0 && (
                <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                  <span className="text-muted-foreground">AI suggests:</span>
                  <button
                    type="button"
                    onClick={() => setAsgFee(match.fee_suggestion.suggested_fee)}
                    className="text-purple-700 hover:underline font-mono"
                    title={(match.fee_suggestion.reasoning || []).join(" · ")}
                    data-testid="apply-fee-suggestion"
                  >
                    {money(match.fee_suggestion.suggested_fee)} ⤵
                  </button>
                  <span className="text-muted-foreground truncate">
                    (spread {money(match.fee_suggestion.spread)} × 15%)
                  </span>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Scheduled close date (optional)</Label>
              <Input type="date" value={asgClose} onChange={(e) => setAsgClose(e.target.value)}
                     className="mt-1" data-testid="dispo-assign-close" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAsgOpen(false)}>Cancel</Button>
            <Button
              onClick={downloadAssignment}
              disabled={busy}
              className="rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
              data-testid="dispo-assign-download"
            >
              {busy ? <Loader2 size={13} className="animate-spin mr-2" /> : <Handshake size={13} className="mr-2" />}
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
